// Signups & trials — replaces Trial Management.
//
//   Needs review   personal-email signups held before anything is built
//                  (work emails with a confirmed address never land here)
//   Setting up     workspaces being built, with the step timeline and retry
//   Active trials  grouped by how soon they end: log in as, remind, extend
//   Declined & expired
//
// Everything the old Trial Management page did is still here: urgency groups,
// tier and days-left, "log in as" (generate-access-link), the reminder (now a
// real email instead of a mailto: link), and a way into the company record.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowPathIcon, ArrowTopRightOnSquareIcon, CheckIcon, ExclamationTriangleIcon, EnvelopeIcon, XMarkIcon,
  ShieldCheckIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import api from '../../api/axios'
import type { ComposerOptions, SignupRow, SignupsResponse } from './types'
import { PORTAL_DOMAIN, ago, errMsg, shortDate, tierLabel } from './types'
import Timeline, { StepBar, stepSummary } from './Timeline'
import ApprovalDrawer from './ApprovalDrawer'
import ActionDialog, { type ActionKind } from './ActionDialog'

type Tab = 'review' | 'setup' | 'trials' | 'closed'

const TIER_BADGE: Record<string, string> = {
  enterprise: 'bg-violet-100 text-violet-700',
  premium: 'bg-blue-100 text-blue-700',
  standard: 'bg-slate-100 text-slate-700',
  basic: 'bg-slate-50 text-slate-500 border border-slate-200',
}

function Signal({ tone, children }: { tone: 'ok' | 'warn' | 'bad' | 'plain'; children: React.ReactNode }) {
  const cls = tone === 'ok' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
    : tone === 'bad' ? 'text-red-700 bg-red-50 border-red-200'
    : 'text-slate-600 bg-slate-50 border-slate-200'
  return <span className={`inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>
}

function Btn({ children, onClick, tone = 'plain', disabled, title }: { children: React.ReactNode; onClick: () => void; tone?: 'plain' | 'pri' | 'good' | 'danger'; disabled?: boolean; title?: string }) {
  const cls = tone === 'pri' ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
    : tone === 'good' ? 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700'
    : tone === 'danger' ? 'bg-white border-red-200 text-red-600 hover:bg-red-50'
    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
  return <button type="button" title={title} onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-[12.5px] font-semibold whitespace-nowrap disabled:opacity-50 ${cls}`}>{children}</button>
}

export default function SignupsTrials() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [data, setData] = useState<SignupsResponse | null>(null)
  const [options, setOptions] = useState<ComposerOptions | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [toast, setToast] = useState<{ msg: string; tone: 'ok' | 'error' } | null>(null)
  const [approving, setApproving] = useState<SignupRow | null>(null)
  const [action, setAction] = useState<{ kind: ActionKind; row: SignupRow } | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await api.get('/super-admin/signups')
      setData(r.data)
      setLoadError('')
    } catch (err) {
      setLoadError(errMsg(err, "Couldn't load signups"))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    api.get('/super-admin/signups/options').then((r) => setOptions(r.data)).catch(() => {})
  }, [])

  // Keep the setup timeline live while anything is building.
  const building = !!data?.setup.some((r) => r.provisioningStatus === 'provisioning')
  useEffect(() => {
    if (!building) return
    const t = setInterval(load, 4000)
    return () => clearInterval(t)
  }, [building, load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 4500)
    return () => clearTimeout(t)
  }, [toast])

  const notify = useCallback((msg: string, tone: 'ok' | 'error' = 'ok') => setToast({ msg, tone }), [])

  const counts = data?.counts
  const defaultTab: Tab = counts?.review ? 'review' : counts?.failed ? 'setup' : 'trials'
  const tab = (params.get('tab') as Tab) || defaultTab
  const setTab = (t: Tab) => setParams((p) => { p.set('tab', t); return p }, { replace: true })

  const retry = async (row: SignupRow, restart = false) => {
    setBusyId(row.id)
    try {
      await api.post(`/super-admin/signups/${row.id}/retry`, { restart })
      notify(`Retrying setup for ${row.companyName}`)
      setTimeout(load, 800)
    } catch (err) {
      notify(errMsg(err, "Couldn't retry"), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const loginAs = async (row: SignupRow) => {
    setBusyId(row.id)
    try {
      const r = await api.post(`/super-admin/companies/${row.id}/generate-access-link`)
      const url = r.data?.access_url || r.data?.url
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
      else notify('No access link came back from the server', 'error')
    } catch (err) {
      notify(errMsg(err, "Couldn't create an access link"), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const trialGroups = useMemo(() => {
    const list = data?.trials ?? []
    return [
      { key: 'critical', label: 'Ending within 7 days', tone: 'text-red-700', rows: list.filter((r) => (r.daysLeft ?? 99) <= 7) },
      { key: 'warning', label: 'Ending within 14 days', tone: 'text-amber-700', rows: list.filter((r) => (r.daysLeft ?? 99) > 7 && (r.daysLeft ?? 99) <= 14) },
      { key: 'normal', label: 'Active trials', tone: 'text-slate-700', rows: list.filter((r) => (r.daysLeft ?? 99) > 14) },
    ].filter((g) => g.rows.length)
  }, [data?.trials])

  const tabs: Array<{ key: Tab; label: string; badge?: React.ReactNode }> = [
    { key: 'review', label: 'Needs review', badge: counts?.review ? <span className="min-w-5 h-5 px-1.5 rounded-full text-[11.5px] font-bold bg-amber-100 text-amber-700 inline-flex items-center justify-center">{counts.review}</span> : null },
    { key: 'setup', label: 'Setting up', badge: counts?.failed ? <span className="h-5 px-1.5 rounded-full text-[11.5px] font-bold bg-red-100 text-red-700 inline-flex items-center">{counts.failed} failed</span> : counts?.setup ? <span className="min-w-5 h-5 px-1.5 rounded-full text-[11.5px] font-bold bg-slate-100 text-slate-600 inline-flex items-center justify-center">{counts.setup}</span> : null },
    { key: 'trials', label: 'Active trials', badge: counts?.trials ? <span className="min-w-5 h-5 px-1.5 rounded-full text-[11.5px] font-bold bg-slate-100 text-slate-600 inline-flex items-center justify-center">{counts.trials}</span> : null },
    { key: 'closed', label: 'Declined & expired' },
  ]

  const Company = ({ r, sub }: { r: SignupRow; sub?: React.ReactNode }) => (
    <div className="min-w-0">
      <div className="font-bold text-[14.5px] text-slate-900 truncate">{r.companyName}</div>
      <div className="text-xs text-slate-500 truncate"><span className="font-mono">{r.slug}.{PORTAL_DOMAIN}</span>{r.industry ? ` · ${r.industry}` : ''}{r.employeeCount ? ` · ${r.employeeCount}+ people` : ''}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  )
  const Contact = ({ r }: { r: SignupRow }) => (
    <div className="min-w-0">
      <div className="font-semibold text-sm text-slate-800 truncate">{r.contact.name || '—'}</div>
      <div className="text-xs text-slate-500 truncate">{r.contact.email}{r.contact.phone ? ` · ${r.contact.phone}` : ''}</div>
    </div>
  )

  const empty = (text: string) => (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">{text}</div>
  )

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Signups &amp; trials</h1>
          <p className="text-sm text-slate-600 mt-1">Approve new companies, watch their setup, and follow up before trials end.</p>
        </div>
        <div className="flex gap-2">
          <Btn onClick={() => navigate('/email-templates')}><EnvelopeIcon className="w-4 h-4" />Email templates</Btn>
          <Btn onClick={() => navigate('/companies?filter=trial')}><BuildingOfficeIcon className="w-4 h-4" />All trial companies</Btn>
          <Btn onClick={() => { setLoading(true); load() }}><ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Btn>
        </div>
      </div>

      <div role="tablist" aria-label="Signup status" className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-3.5 py-2.5 -mb-px border-b-2 text-sm font-semibold whitespace-nowrap ${tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
            {t.label}{t.badge}
          </button>
        ))}
      </div>

      {loadError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError}</div>}
      {loading && !data && <div className="py-16 text-center text-sm text-slate-500">Loading…</div>}

      {data && tab === 'review' && (
        <div className="space-y-3">
          {data.review.length === 0 ? empty('Nothing waiting. Signups from a personal email land here before their workspace is built.') : (
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {data.review.map((r) => (
                <div key={r.id} className="grid gap-4 px-4 py-3.5 items-center lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1.1fr)_minmax(200px,1.2fr)_auto]">
                  <Company r={r} sub={<>Signed up {ago(r.createdAt)}{r.review?.decision === 'asked' ? <span className="text-amber-700"> · you asked for info {ago(r.review.at)}</span> : null}</>} />
                  <Contact r={r} />
                  <div className="flex flex-wrap gap-1.5">
                    {r.signals.personalEmail && <Signal tone="warn"><ExclamationTriangleIcon className="w-3.5 h-3.5" />Personal email</Signal>}
                    <Signal tone={r.signals.emailConfirmed ? 'ok' : 'bad'}>{r.signals.emailConfirmed ? <CheckIcon className="w-3.5 h-3.5" /> : <XMarkIcon className="w-3.5 h-3.5" />}{r.signals.emailConfirmed ? 'Email confirmed' : 'Email not confirmed'}</Signal>
                    {r.signals.captcha && <Signal tone="ok"><CheckIcon className="w-3.5 h-3.5" />Captcha</Signal>}
                    {r.signals.ip && <Signal tone="plain">IP {r.signals.ip}</Signal>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 lg:justify-end">
                    <Btn onClick={() => setAction({ kind: 'ask', row: r })}>Ask for info</Btn>
                    <Btn tone="danger" onClick={() => setAction({ kind: 'decline', row: r })}>Decline</Btn>
                    <Btn tone="pri" disabled={!options} onClick={() => setApproving(r)}>Review &amp; approve</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 items-start rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-slate-700">
            <ShieldCheckIcon className="w-5 h-5 text-blue-600 flex-none" />
            <div>Work-email signups with a confirmed address are set up automatically and never land here. Disposable addresses, blocklisted emails and bots are turned away at the form, before anything is created.</div>
          </div>
        </div>
      )}

      {data && tab === 'setup' && (
        data.setup.length === 0 ? empty('Nothing is being set up right now.') : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {data.setup.map((r) => {
              const sum = stepSummary(r.steps)
              const open = expanded === r.id
              return (
                <div key={r.id} className="px-4 py-3.5">
                  <div className="grid gap-4 items-center lg:grid-cols-[minmax(220px,1.3fr)_minmax(260px,1.5fr)_minmax(180px,1fr)_auto]">
                    <Company r={r} sub={r.review?.decision === 'approved' ? `Approved by ${r.review.by} ${ago(r.review.at)}` : `Signed up ${ago(r.createdAt)}`} />
                    <div className="min-w-0">
                      <StepBar steps={r.steps} />
                      <div className={`text-xs mt-1.5 truncate ${sum.tone === 'bad' ? 'text-red-600' : sum.tone === 'run' ? 'text-blue-700' : 'text-slate-500'}`} title={sum.text}>{sum.text}</div>
                    </div>
                    <Contact r={r} />
                    <div className="flex flex-wrap gap-1.5 lg:justify-end">
                      <Btn onClick={() => setExpanded(open ? null : r.id)}>{open ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}Details</Btn>
                      {r.provisioningStatus === 'failed' && <Btn tone="pri" disabled={busyId === r.id} onClick={() => retry(r)}><ArrowPathIcon className="w-4 h-4" />Retry from here</Btn>}
                    </div>
                  </div>
                  {open && (
                    <div className="mt-3 max-w-3xl">
                      <Timeline steps={r.steps} onRetry={(restart) => retry(r, restart)} retrying={busyId === r.id} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      )}

      {data && tab === 'trials' && (
        trialGroups.length === 0 ? empty('No companies are on a trial right now.') : (
          <div className="space-y-5">
            {trialGroups.map((g) => (
              <section key={g.key} aria-label={g.label}>
                <h2 className={`text-sm font-semibold mb-2 ${g.tone}`}>{g.label} <span className="text-slate-400 font-normal">· {g.rows.length}</span></h2>
                <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {g.rows.map((r) => (
                    <div key={r.id} className="grid gap-4 px-4 py-3 items-center lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(170px,.9fr)_auto]">
                      <Company r={r} />
                      <Contact r={r} />
                      <div className="text-xs text-slate-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${TIER_BADGE[r.tier] || TIER_BADGE.basic}`}>{tierLabel(r.tier || 'basic')}</span>
                          <span className={`inline-flex items-center gap-1 font-semibold ${(r.daysLeft ?? 99) <= 7 ? 'text-red-600' : (r.daysLeft ?? 99) <= 14 ? 'text-amber-700' : 'text-slate-700'}`}>
                            <ClockIcon className="w-3.5 h-3.5" />{r.daysLeft == null ? 'No end date' : r.daysLeft <= 0 ? 'Ends today' : `${r.daysLeft} day${r.daysLeft === 1 ? '' : 's'} left`}
                          </span>
                        </div>
                        <div>Ends {shortDate(r.trialEndsAt)} · last sign-in {r.lastSignInAt ? ago(r.lastSignInAt) : 'never'}</div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 lg:justify-end">
                        <Btn disabled={busyId === r.id} onClick={() => loginAs(r)} title="Opens their portal in a new tab"><ArrowTopRightOnSquareIcon className="w-4 h-4" />Log in as</Btn>
                        <Btn onClick={() => setAction({ kind: 'remind', row: r })}><EnvelopeIcon className="w-4 h-4" />Remind</Btn>
                        <Btn onClick={() => setAction({ kind: 'extend', row: r })}>Extend</Btn>
                        <Btn onClick={() => navigate(`/companies#company=${r.id}`)}>Open</Btn>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )
      )}

      {data && tab === 'closed' && (
        data.closed.length === 0 ? empty('Nothing declined or expired.') : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {data.closed.map((r) => (
              <div key={r.id} className="grid gap-4 px-4 py-3 items-center lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(220px,1.2fr)_auto]">
                <Company r={r} />
                <Contact r={r} />
                <div className="text-xs text-slate-600">
                  {r.provisioningStatus === 'declined'
                    ? <>Declined {r.review ? `by ${r.review.by} ${ago(r.review.at)}` : ''}{r.review?.note ? <span className="block text-slate-500 truncate" title={r.review.note}>“{r.review.note}”</span> : null}</>
                    : <>Trial {r.status === 'expired' ? 'expired' : 'suspended'} · ended {shortDate(r.trialEndsAt)}</>}
                </div>
                <div className="flex flex-wrap gap-1.5 lg:justify-end">
                  {r.provisioningStatus !== 'declined' && <Btn onClick={() => setAction({ kind: 'extend', row: r })}>Reopen trial</Btn>}
                  <Btn onClick={() => navigate(`/companies#company=${r.id}`)}>Open</Btn>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {approving && options && (
        <ApprovalDrawer row={approving} options={options} onClose={() => setApproving(null)} onApproved={() => { setTab('setup'); load() }} notify={notify} />
      )}
      {action && <ActionDialog kind={action.kind} row={action.row} onClose={() => setAction(null)} onDone={load} notify={notify} />}

      {toast && (
        <div role="status" className={`fixed bottom-5 right-5 z-[70] max-w-sm rounded-xl px-4 py-3 text-sm font-medium shadow-lg ${toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>{toast.msg}</div>
      )}
    </div>
  )
}
